/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Public Entra and Platform identifiers required at build time so the Word
 * shell, manifest, and Platform Office SSO exchange stay aligned (PMI-INFRA
 * delivery values, baked in via `VITE_*`).
 *
 * @see ECSP-MS365-5.5-INT-004
 */

/** Keys that MUST be non-empty strings in `import.meta.env` before Office SSO runs. */
export const OFFICE_SSO_REQUIRED_VITE_KEYS = [
  'VITE_API_URL',
  'VITE_ADDIN_URL',
  'VITE_OFFICE_SSO_CLIENT_ID',
  'VITE_OFFICE_SSO_APPLICATION_ID_URI',
  'VITE_OFFICE_SSO_SCOPE',
  'VITE_TENANT_ID',
  'VITE_PLATFORM_API_AUDIENCE',
  'VITE_PLATFORM_API_SCOPE',
] as const;

export type OfficeSsoRequiredViteKey = (typeof OFFICE_SSO_REQUIRED_VITE_KEYS)[number];

function readOfficeSsoEnvSnapshot(): Record<OfficeSsoRequiredViteKey, string | undefined> {
  return {
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_ADDIN_URL: import.meta.env.VITE_ADDIN_URL,
    VITE_OFFICE_SSO_CLIENT_ID: import.meta.env.VITE_OFFICE_SSO_CLIENT_ID,
    VITE_OFFICE_SSO_APPLICATION_ID_URI: import.meta.env.VITE_OFFICE_SSO_APPLICATION_ID_URI,
    VITE_OFFICE_SSO_SCOPE: import.meta.env.VITE_OFFICE_SSO_SCOPE,
    VITE_TENANT_ID: import.meta.env.VITE_TENANT_ID,
    VITE_PLATFORM_API_AUDIENCE: import.meta.env.VITE_PLATFORM_API_AUDIENCE,
    VITE_PLATFORM_API_SCOPE: import.meta.env.VITE_PLATFORM_API_SCOPE,
  };
}

export function collectMissingOfficeSsoEnvKeys(
  env: Record<OfficeSsoRequiredViteKey, string | undefined>,
): readonly OfficeSsoRequiredViteKey[] {
  const missing: OfficeSsoRequiredViteKey[] = [];
  for (const key of OFFICE_SSO_REQUIRED_VITE_KEYS) {
    const v = readEnvKey(env, key);
    if (typeof v !== 'string' || v.trim() === '') {
      missing.push(key);
    }
  }
  return missing;
}

function readEnvKey(
  env: Record<OfficeSsoRequiredViteKey, string | undefined>,
  key: OfficeSsoRequiredViteKey,
): string | undefined {
  switch (key) {
    case 'VITE_API_URL':
      return env.VITE_API_URL;
    case 'VITE_ADDIN_URL':
      return env.VITE_ADDIN_URL;
    case 'VITE_OFFICE_SSO_CLIENT_ID':
      return env.VITE_OFFICE_SSO_CLIENT_ID;
    case 'VITE_OFFICE_SSO_APPLICATION_ID_URI':
      return env.VITE_OFFICE_SSO_APPLICATION_ID_URI;
    case 'VITE_OFFICE_SSO_SCOPE':
      return env.VITE_OFFICE_SSO_SCOPE;
    case 'VITE_TENANT_ID':
      return env.VITE_TENANT_ID;
    case 'VITE_PLATFORM_API_AUDIENCE':
      return env.VITE_PLATFORM_API_AUDIENCE;
    case 'VITE_PLATFORM_API_SCOPE':
      return env.VITE_PLATFORM_API_SCOPE;
    default: {
      const exhaustive: never = key;
      return exhaustive;
    }
  }
}

/**
 * Ensures Stage 5.5 Office SSO build-time configuration is present before
 * `OfficeRuntime.auth` or Platform exchange runs. Missing values usually mean
 * the CI or local Vite environment was not given the public Entra identifiers.
 */
export function assertOfficeSsoBuildConfig(): void {
  const missing = collectMissingOfficeSsoEnvKeys(readOfficeSsoEnvSnapshot());
  if (missing.length === 0) {
    return;
  }
  throw new Error(
    `Office SSO is not configured for this build: missing ${missing.join(', ')}. Set these Vite environment variables at build time (see apps/word/.env.development).`,
  );
}
